import { t } from "@travily/i18n";
import { PersistentSheet } from "@ui/components/PersistentSheet";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { FRAME_TOPBAR_HEIGHT_VAR } from "@/lib/frame";
import { MOBILE_DOCK_OFFSET, MOBILE_PEEK_PX, MOBILE_PINCARD_GAP } from "@/lib/mobile-search-map";

// Detents in ascending openness (1-based, per PersistentSheet): peek (header
// only) → half → full (implicit; the sheet slides behind the sticky TopBar —
// z-40 over the sheet's z-20 — so expanded content scrolls behind the bar).
const DETENT_PEEK = 1;
const DETENT_HALF = 2;

export interface MobileSearchMapProps {
  /** Shared teleport ref from `useSearchResultsMap` — the full-bleed map mounts here. */
  mapSlotRef: (el: HTMLDivElement | null) => void;
  /** Accessible name for the results sheet (the translated "N results" heading text). */
  label: string;
  /** Visible sheet header (heading + optional sort control). */
  header: ReactNode;
  /** The result card list. */
  children: ReactNode;
  /** Floating pin card shown above the peeked sheet when a pin is selected; `null` hides it. */
  pinCard: ReactNode | null;
  /** Clears the pin selection when the user drags the sheet up off the peek detent. */
  onDismissPin?: () => void;
}

/**
 * Airbnb-style mobile search surface: a full-viewport map behind a draggable snap-point sheet of
 * result cards, plus a floating pin card. Selecting a pin collapses the sheet to peek so the map
 * and card are visible; dragging the sheet back up dismisses the pin. Rendered only below `lg`
 * (the desktop grid handles wider viewports).
 */
export function MobileSearchMap({ mapSlotRef, label, header, children, pinCard, onDismissPin }: MobileSearchMapProps) {
  // The floating Dock (bottom tab bar) overlays the sheet rather than sitting beside it, so the
  // track runs to the viewport bottom; only the scrolled list reserves clearance for it, and only
  // below `md`, where the Dock renders.
  const hasDock = !useBreakpoint("md");

  // The Dock overlays the sheet's bottom edge, so every measurement taken from the viewport
  // bottom grows by its footprint to keep the same amount of sheet visible above it.
  const dockOffset = hasDock ? MOBILE_DOCK_OFFSET : "0px";

  // Half = 50% of the region between the top bar and the Dock, then lifted to sit on top of the
  // Dock — so it stays above the peek stop, which grows by the same footprint. Silk detents are
  // CSS lengths (no %), so express it as a calc against the viewport var. (The track itself runs
  // to the viewport top — see below.)
  const halfDetent = `calc(${dockOffset} + (var(--height-viewport) - ${FRAME_TOPBAR_HEIGHT_VAR} - ${dockOffset}) / 2)`;
  const stops = [
    {
      label: t("components.frames.app_layout.results_sheet.collapsed"),
      length: `calc(${MOBILE_PEEK_PX}px + ${dockOffset})`,
    },
    { label: t("components.frames.app_layout.results_sheet.half_open"), length: halfDetent },
  ];

  const [detent, setDetent] = useState(DETENT_HALF);
  const hasPinCard = pinCard != null;
  const hadPinCardRef = useRef(false);

  // Collapse to peek when a pin is newly selected (so the map + floating card show).
  useEffect(() => {
    if (hasPinCard && !hadPinCardRef.current) setDetent(DETENT_PEEK);
    hadPinCardRef.current = hasPinCard;
  }, [hasPinCard]);

  const handleDetentChange = useCallback(
    (index: number) => {
      setDetent(index);
      if (index !== DETENT_PEEK && hasPinCard) onDismissPin?.();
    },
    [hasPinCard, onDismissPin],
  );

  return (
    <>
      <div
        ref={mapSlotRef}
        className="fixed inset-x-0 bottom-0 z-0 bg-neutral-100"
        style={{ top: FRAME_TOPBAR_HEIGHT_VAR }}
      />

      {pinCard != null ? (
        <div
          className="pointer-events-none fixed inset-x-4 z-[25] flex justify-center"
          style={{ bottom: `calc(${MOBILE_PEEK_PX + MOBILE_PINCARD_GAP}px + ${dockOffset})` }}
        >
          <div className="pointer-events-auto w-full max-w-[420px]">{pinCard}</div>
        </div>
      ) : null}

      <PersistentSheet
        activeDetent={detent}
        aria-label={label}
        bottomOffset="0px"
        defaultDetent={DETENT_HALF}
        expandedLabel={t("components.frames.app_layout.results_sheet.expanded")}
        header={header}
        // Raised above peek the sheet is modal (the Safari-reliable
        // configuration for detent travel + in-sheet scrolling); the fixed
        // chrome around it stays tappable via Islands. The map needs no
        // Island: it is fully interactive at peek, where the sheet is
        // non-modal. The Dock's Island now sits over the sheet, so gestures
        // in that band drive the nav rather than sheet travel — deliberate:
        // the Dock floats above the sheet.
        interactiveOutside={['[data-frame-chrome="topbar"]', '[data-frame-chrome="bottombar"]']}
        onActiveDetentChange={handleDetentChange}
        stops={stops}
      >
        <div style={hasDock ? { paddingBottom: MOBILE_DOCK_OFFSET } : undefined}>{children}</div>
      </PersistentSheet>
    </>
  );
}
